import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, { Controls, Background, MiniMap, Node, Edge } from 'reactflow';
import { Form, Button, Spinner } from 'react-bootstrap';
import { fetchViewerData, clearViewerData } from '../../../../../redux/slices/monitoringDashboardsSlice';
import FlowCardNode from './components/FlowCardNode';
import EventConnectionEdge from './components/EventConnectionEdge';
import './MonitoringDashboard.scss';
import 'reactflow/dist/style.css';

const MonitoringDashboardViewer: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { viewerData, loading, error } = useAppSelector((state) => state.monitoringDashboards);

  // ---------- Date range state ----------
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  });

  const [dateTo, setDateTo] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  });

  // ---------- Node & edge type registrations (stable refs) ----------
  const nodeTypes = useMemo(() => ({ flowCard: FlowCardNode }), []);
  const edgeTypes = useMemo(() => ({ eventConnection: EventConnectionEdge }), []);

  // ---------- Initial fetch & refetch on date change ----------
  useEffect(() => {
    if (dashboardId) {
      dispatch(
        fetchViewerData({
          dashboard_id: parseInt(dashboardId),
          date_from: dateFrom,
          date_to: dateTo,
        }) as any
      );
    }
  }, [dashboardId, dateFrom, dateTo, dispatch]);

  // ---------- Auto-refresh every 30 seconds ----------
  useEffect(() => {
    const interval = setInterval(() => {
      if (dashboardId) {
        dispatch(
          fetchViewerData({
            dashboard_id: parseInt(dashboardId),
            date_from: dateFrom,
            date_to: dateTo,
          }) as any
        );
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [dashboardId, dateFrom, dateTo, dispatch]);

  // ---------- Cleanup on unmount ----------
  useEffect(() => {
    return () => {
      dispatch(clearViewerData());
    };
  }, [dispatch]);

  // ---------- Map viewer data to ReactFlow nodes ----------
  const flowNodes: Node[] = useMemo(() => {
    if (!viewerData?.items) return [];
    return viewerData.items.map((item: any) => ({
      id: `nd-${item.node_definition_id}`,
      type: 'flowCard',
      position: { x: item.position_x, y: item.position_y },
      draggable: false,
      data: {
        label: item.node_definition_name,
        mode: 'viewer',
        hasInstance: !!item.flow_instance,
        flowInstanceStatus: item.flow_instance?.status || null,
        scheduledTime: item.flow_instance?.scheduled_start_datetime || item.flow_instance?.effective_datetime || null,
        childNodes: (item.child_node_instances || []).map((child: any) => ({
          flow_node_id: child.node_id,
          name: child.node_definition_name,
          type: child.type,
          status: child.status,
          sub_status: child.sub_status,
        })),
      },
    }));
  }, [viewerData]);

  // ---------- Map event connections to ReactFlow edges ----------
  const flowEdges: Edge[] = useMemo(() => {
    if (!viewerData?.event_connections) return [];
    return viewerData.event_connections.map((conn: any) => ({
      id: `event-${conn.event_id}-${conn.source_node_definition_id}-${conn.target_node_definition_id}`,
      source: `nd-${conn.source_node_definition_id}`,
      target: `nd-${conn.target_node_definition_id}`,
      type: 'eventConnection',
      data: { label: conn.event_name },
    }));
  }, [viewerData]);

  // ---------- Date display / change handlers ----------
  const dateFromDisplay = dateFrom ? new Date(dateFrom).toISOString().split('T')[0] : '';
  const dateToDisplay = dateTo ? new Date(dateTo).toISOString().split('T')[0] : '';

  const handleDateFromChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const d = new Date(val);
    if (isNaN(d.getTime())) return;
    d.setHours(0, 0, 0, 0);
    setDateFrom(d.toISOString());
  }, []);

  const handleDateToChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const d = new Date(val);
    if (isNaN(d.getTime())) return;
    d.setHours(23, 59, 59, 999);
    setDateTo(d.toISOString());
  }, []);

  // ---------- Initial loading state ----------
  if (!viewerData && loading.fetchViewerData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  // ---------- Error state ----------
  if (!viewerData && error && !loading.fetchViewerData) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center gap-3" style={{ height: '60vh' }}>
        <div className="text-danger fw-semibold">Failed to load dashboard data</div>
        <div className="text-muted" style={{ fontSize: '0.875rem' }}>
          {error}
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (dashboardId) {
                dispatch(
                  fetchViewerData({
                    dashboard_id: parseInt(dashboardId),
                    date_from: dateFrom,
                    date_to: dateTo,
                  }) as any
                );
              }
            }}
          >
            Retry
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => navigate('/monitoring/dashboards')}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  // ---------- Render ----------
  return (
    <div>
      <div className="monitoring-viewer-toolbar">
        <h5 className="mb-0">{viewerData?.dashboard?.name || 'Loading...'}</h5>
        <div className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0 text-nowrap">From:</Form.Label>
          <Form.Control type="date" value={dateFromDisplay} onChange={handleDateFromChange} style={{ width: 160 }} />
          <Form.Label className="mb-0 text-nowrap">To:</Form.Label>
          <Form.Control type="date" value={dateToDisplay} onChange={handleDateToChange} style={{ width: 160 }} />
        </div>
        {loading.fetchViewerData && <Spinner animation="border" size="sm" />}
        <div className="monitoring-status-legend">
          <span className="legend-item">
            <span className="legend-dot completed"></span> Completed
          </span>
          <span className="legend-item">
            <span className="legend-dot failed"></span> Failed
          </span>
          <span className="legend-item">
            <span className="legend-dot running"></span> Running
          </span>
          <span className="legend-item">
            <span className="legend-dot pending"></span> Pending
          </span>
          <span className="legend-item">
            <span className="legend-dot waiting"></span> Waiting
          </span>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/monitoring/dashboards')}>
          Back
        </Button>
      </div>
      <div className="monitoring-viewer-canvas">
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          proOptions={{ hideAttribution: true }}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Background />
          <Controls position="top-left" />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
};

export default MonitoringDashboardViewer;
